import React from 'react';
import { X, Download } from 'lucide-react';
import { AppSettings, DEFAULT_SETTINGS } from '../utils/settings';
import { LayoutDirection, PercentageDisplayMode } from '../types';
import './SettingsPanel.css';

interface SettingsPanelProps {
  settings: AppSettings;
  onChange: (next: AppSettings) => void;
  onClose: () => void;
}

interface ToggleProps {
  on: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
}

const Toggle: React.FC<ToggleProps> = ({ on, onChange, ariaLabel }) => (
  <button
    type="button"
    className={`sp-toggle${on ? ' on' : ''}`}
    onClick={() => onChange(!on)}
    aria-pressed={on}
    aria-label={ariaLabel}
  />
);

const layoutOptions: Array<{ value: LayoutDirection; label: string }> = [
  { value: 'TB', label: 'Boven ↓ Onder' },
  { value: 'BT', label: 'Onder ↑ Boven' },
  { value: 'LR', label: 'Links → Rechts' },
  { value: 'RL', label: 'Rechts ← Links' },
];

const SettingsPanel: React.FC<SettingsPanelProps> = ({ settings, onChange, onClose }) => {
  const update = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <aside className="settings-panel">
      <div className="sp-header">
        <h2>Instellingen</h2>
        <button type="button" className="sp-close" onClick={onClose} aria-label="Instellingen sluiten">
          <X size={18} />
        </button>
      </div>

      <div className="sp-body">
        <div className="sp-group">
          <h3>Weergave</h3>

          <div className="sp-row">
            <label>Grid achtergrond</label>
            <Toggle
              on={settings.showGrid}
              onChange={(v) => update('showGrid', v)}
              ariaLabel="Grid achtergrond"
            />
          </div>

          <div className="sp-row">
            <label htmlFor="sp-layout-direction">Layout-richting</label>
            <select
              id="sp-layout-direction"
              value={settings.layoutDirection}
              onChange={(e) => update('layoutDirection', e.target.value as LayoutDirection)}
            >
              {layoutOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sp-row">
            <label>Land weergave</label>
            <div className="sp-radio-group">
              <button
                type="button"
                className={`sp-radio-btn${settings.showCountryFlags ? ' active' : ''}`}
                onClick={() => update('showCountryFlags', true)}
              >
                Vlag
              </button>
              <button
                type="button"
                className={`sp-radio-btn${!settings.showCountryFlags ? ' active' : ''}`}
                onClick={() => update('showCountryFlags', false)}
              >
                Code
              </button>
            </div>
          </div>

          <div className="sp-row">
            <label>Percentage</label>
            <div className="sp-radio-group">
              {(['card', 'line', 'both'] as PercentageDisplayMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={`sp-radio-btn${settings.percentageDisplayMode === mode ? ' active' : ''}`}
                  onClick={() => update('percentageDisplayMode', mode)}
                >
                  {mode === 'card' ? 'Kaart' : mode === 'line' ? 'Lijn' : 'Beide'}
                </button>
              ))}
            </div>
          </div>

          <div className="sp-row">
            <label>Enig aandeelhouder in kaart</label>
            <Toggle
              on={settings.showSingleShareholder}
              onChange={(v) => update('showSingleShareholder', v)}
              ariaLabel="Enig aandeelhouder in kaart"
            />
          </div>

          <div className="sp-row">
            <label>Bestuurders in kaart</label>
            <Toggle
              on={settings.showDirectors}
              onChange={(v) => update('showDirectors', v)}
              ariaLabel="Bestuurders in kaart"
            />
          </div>

          <div className="sp-row">
            <label>Cluster groepering</label>
            <Toggle
              on={settings.clusterGrouping}
              onChange={(v) => update('clusterGrouping', v)}
              ariaLabel="Cluster groepering"
            />
          </div>
        </div>
      </div>

      <div className="sp-footer">
        <a
          href="/template.xlsx"
          download="demo-orgchart-50.xlsx"
          className="sp-template-link"
          title="Download een voorbeeld Excel-template"
        >
          <Download size={14} />
          <span>Template</span>
        </a>
        <button
          type="button"
          className="sp-reset-btn"
          onClick={() => onChange({ ...DEFAULT_SETTINGS })}
        >
          Standaard herstellen
        </button>
      </div>
    </aside>
  );
};

export default SettingsPanel;
